import { useState } from 'react';
import { Button, InfoBox, Modal } from '@immo/ui-kit';
import type { FactKeyDetailView, FactKeyView } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';

const ALREADY_RETIRED_CODE = 'property.fact-key.already-retired';
const NOT_FOUND_CODE = 'property.fact-key.not-found';

type Props = {
  readonly detail: FactKeyDetailView;
  readonly onClose: () => void;
  readonly onRetired: (view: FactKeyView) => void;
};

export function RetireFactKeyDialog({ detail, onClose, onRetired }: Props) {
  const t = useTranslations('pages.admin.skills.fact-keys');
  const tErrors = useTranslations('errors');
  const [retiring, setRetiring] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async (): Promise<void> => {
    setRetiring(true);
    setError(null);

    try {
      const result = await apiClient.adminFactKeys.retire({ pathParams: { key: detail.key } });

      if (result.ok) {
        onRetired(result.body);

        return;
      }

      const code = (result.body as { error?: { code?: string } } | undefined)?.error?.code;
      setError(code === ALREADY_RETIRED_CODE || code === NOT_FOUND_CODE ? tErrors(code) : t('save-error'));
    } finally {
      setRetiring(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      stacked
      title={t('retire.title')}
      footer={(requestClose) => (
        <>
          <Button type="button" variant="ghost" onClick={requestClose}>
            {t('retire.cancel')}
          </Button>
          <Button type="button" variant="danger" disabled={retiring} onClick={() => void confirm()}>
            {t('retire.confirm')}
          </Button>
        </>
      )}
    >
      <div className="space-y-3">
        {error !== null && <InfoBox type="danger">{error}</InfoBox>}
        <p className="text-sm text-text-secondary">{t('retire.body', { label: detail.label, key: detail.key })}</p>
        {detail.usage.filledByDocumentAnalysis && (
          <InfoBox type="warning">{t('retire.warning-document-analysis')}</InfoBox>
        )}
        {detail.propertyFieldKey !== null && (
          <InfoBox type="warning">
            {t('retire.warning-property-field', { propertyField: detail.propertyFieldKey })}
          </InfoBox>
        )}
      </div>
    </Modal>
  );
}
